import { db } from '../config/firebase';
import { collection, getDocs } from 'firebase/firestore';

/**
 * Search result types
 */
export const SEARCH_RESULT_TYPES = {
  CAMPAIGN_EPISODE: 'campaign_episode',
  POST: 'post',
  MAP: 'map',
  PIN: 'pin',
  ACCOLADE: 'accolade',
  JOURNAL: 'journal',
  JOURNAL_PDF_CONTENT: 'journal_pdf_content',
  WIKI: 'wiki'
};

/**
 * Performs a unified search across all database collections
 * @param {string} searchQuery - The search query string
 * @param {number} limit - Maximum number of results to return (default: 20)
 * @returns {Promise<Array>} Array of search results with type and navigation info
 */
export const performUnifiedSearch = async (searchQuery, limit = 20) => {
  if (!searchQuery || searchQuery.trim().length < 2) {
    return [];
  }

  const query_lower = searchQuery.toLowerCase().trim();
  const results = [];

  try {
    // Search in parallel for better performance
    const [
      campaignResults,
      postResults,
      mapResults,
      pinResults,
      accoladeResults,
      journalResults,
      wikiResults
    ] = await Promise.all([
      searchCampaignEpisodes(query_lower),
      searchPosts(query_lower),
      searchMaps(query_lower),
      searchPins(query_lower),
      searchAccolades(query_lower),
      searchJournalEntries(query_lower),
      searchWikiEntries(query_lower)
    ]);

    results.push(...campaignResults);
    results.push(...postResults);
    results.push(...mapResults);
    results.push(...pinResults);
    results.push(...accoladeResults);
    results.push(...journalResults);
    results.push(...wikiResults);

    // Sort by relevance score (if implemented) or by date
    const sorted = results.sort((a, b) => {
      // Prioritize exact matches
      if (a.exactMatch && !b.exactMatch) return -1;
      if (!a.exactMatch && b.exactMatch) return 1;
      
      // Then sort by relevance score or date
      if (a.createdAt && b.createdAt) {
        return b.createdAt - a.createdAt;
      }
      return 0;
    });

    // Return limited results with metadata about total count
    const hasMore = sorted.length > limit;
    const limitedResults = sorted.slice(0, limit);
    
    // Add metadata to first result if there are more
    if (hasMore && limitedResults.length > 0) {
      limitedResults[0].totalResults = sorted.length;
      limitedResults[0].hasMore = true;
    }
    
    return limitedResults;
  } catch (error) {
    console.error('Error performing unified search:', error);
    throw error;
  }
};

/**
 * Search campaign episodes
 */
const searchCampaignEpisodes = async (query_lower) => {
  try {
    const querySnapshot = await getDocs(collection(db, 'campaignEpisodes'));
    const results = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const title = (data.title || '').toLowerCase();
      const description = (data.description || '').toLowerCase();
      
      if (title.includes(query_lower) || description.includes(query_lower)) {
        results.push({
          id: doc.id,
          type: SEARCH_RESULT_TYPES.CAMPAIGN_EPISODE,
          title: data.title || 'Untitled Episode',
          subtitle: `Episode ${data.episodeNumber || '?'}`,
          description: data.description,
          exactMatch: title === query_lower,
          createdAt: data.createdAt?.toDate?.() || null,
          navigationPath: `/campaign-episodes/${doc.id}`,
          icon: '📺'
        });
      }
    });

    return results;
  } catch (error) {
    console.error('Error searching campaign episodes:', error);
    return [];
  }
};

/**
 * Search Conference Clamp posts (Town Square)
 */
const searchPosts = async (query_lower) => {
  try {
    const querySnapshot = await getDocs(collection(db, 'posts'));
    const results = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const title = (data.title || '').toLowerCase();
      const content = (data.content || '').toLowerCase();
      const userName = (data.userName || '').toLowerCase();
      const characterName = (data.characterName || '').toLowerCase();
      
      if (title.includes(query_lower) || content.includes(query_lower) || 
          userName.includes(query_lower) || characterName.includes(query_lower)) {
        results.push({
          id: doc.id,
          type: SEARCH_RESULT_TYPES.POST,
          title: data.title || 'Untitled Post',
          subtitle: `By ${data.userName || 'Unknown'}${data.characterName ? ` (as ${data.characterName})` : ''}`,
          description: content.substring(0, 100),
          exactMatch: title === query_lower,
          createdAt: data.createdAt?.toDate?.() || null,
          navigationPath: `/post/${doc.id}`,
          icon: '💬'
        });
      }
    });

    return results;
  } catch (error) {
    console.error('Error searching posts:', error);
    return [];
  }
};

/**
 * Search maps
 */
const searchMaps = async (query_lower) => {
  try {
    const querySnapshot = await getDocs(collection(db, 'maps'));
    const results = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const title = (data.title || '').toLowerCase();
      const description = (data.description || '').toLowerCase();
      
      if (title.includes(query_lower) || description.includes(query_lower)) {
        results.push({
          id: doc.id,
          type: SEARCH_RESULT_TYPES.MAP,
          title: data.title || 'Untitled Map',
          subtitle: 'Campaign Map',
          description: data.description,
          exactMatch: title === query_lower,
          createdAt: data.createdAt?.toDate?.() || new Date(data.createdAt || 0),
          navigationPath: `/maps/${doc.id}`,
          icon: '🗺️'
        });
      }
    });

    return results;
  } catch (error) {
    console.error('Error searching maps:', error);
    return [];
  }
};

/**
 * Search map pins
 */
const searchPins = async (query_lower) => {
  try {
    const querySnapshot = await getDocs(collection(db, 'pins'));
    const results = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const title = (data.title || '').toLowerCase();
      const description = (data.description || '').toLowerCase();
      
      if (title.includes(query_lower) || description.includes(query_lower)) {
        results.push({
          id: doc.id,
          type: SEARCH_RESULT_TYPES.PIN,
          title: data.title || 'Untitled Pin',
          subtitle: 'Map Pin',
          description: data.description,
          exactMatch: title === query_lower,
          mapId: data.mapId,
          lat: data.lat,
          lng: data.lng,
          x: data.x, // for interactive maps
          y: data.y, // for interactive maps
          navigationPath: `/maps/${data.mapId}?pinId=${doc.id}`,
          icon: '📍'
        });
      }
    });

    return results;
  } catch (error) {
    console.error('Error searching pins:', error);
    return [];
  }
};

/**
 * Search accolades
 */
const searchAccolades = async (query_lower) => {
  try {
    const querySnapshot = await getDocs(collection(db, 'accolades'));
    const results = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const title = (data.title || '').toLowerCase();
      const description = (data.description || '').toLowerCase();
      const characterName = (data.characterName || '').toLowerCase();
      
      if (title.includes(query_lower) || description.includes(query_lower) || 
          characterName.includes(query_lower)) {
        results.push({
          id: doc.id,
          type: SEARCH_RESULT_TYPES.ACCOLADE,
          title: data.title || 'Untitled Accolade',
          subtitle: `Awarded to ${data.characterName || 'Unknown'}`,
          description: data.description,
          exactMatch: title === query_lower,
          createdAt: data.createdAt?.toDate?.() || null,
          navigationPath: `/trophy-hall`,
          scrollToId: doc.id,
          icon: '🏆'
        });
      }
    });

    return results;
  } catch (error) {
    console.error('Error searching accolades:', error);
    return [];
  }
};

/**
 * Search Benny's Journal entries (including PDF content if available)
 */
const searchJournalEntries = async (query_lower) => {
  try {
    const querySnapshot = await getDocs(collection(db, 'bennyJournalEntries'));
    const results = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const title = (data.title || '').toLowerCase();
      const description = (data.description || '').toLowerCase();
      const pdfTextContent = (data.pdfTextContent || '').toLowerCase();
      
      // Check if search matches title or description
      const titleMatch = title.includes(query_lower);
      const descriptionMatch = description.includes(query_lower);
      const contentMatch = pdfTextContent.includes(query_lower);
      
      if (titleMatch || descriptionMatch) {
        results.push({
          id: doc.id,
          type: SEARCH_RESULT_TYPES.JOURNAL,
          title: data.title || 'Untitled Entry',
          subtitle: "Benny's Journal Entry",
          description: data.description,
          exactMatch: title === query_lower,
          createdAt: data.createdAt?.toDate?.() || null,
          navigationPath: `/benny-journal/${doc.id}`,
          icon: '📔'
        });
      }
      
      // If search matches PDF content, find ALL occurrences
      if (contentMatch && data.downloadURL) {
        // Find all matches in the PDF content
        const matches = [];
        let startIndex = 0;
        
        while (startIndex < pdfTextContent.length) {
          const matchIndex = pdfTextContent.indexOf(query_lower, startIndex);
          if (matchIndex === -1) break;
          
          matches.push(matchIndex);
          startIndex = matchIndex + query_lower.length;
        }
        
        // Create a result for each match (limit to first 5 per document)
        const maxMatchesPerDoc = 5;
        matches.slice(0, maxMatchesPerDoc).forEach((matchIndex, index) => {
          const contextLength = 60; // characters on each side
          const contextStart = Math.max(0, matchIndex - contextLength);
          const contextEnd = Math.min(pdfTextContent.length, matchIndex + query_lower.length + contextLength);
          const context = pdfTextContent.substring(contextStart, contextEnd).trim();
          
          // Add ellipsis if truncated
          const prefix = contextStart > 0 ? '...' : '';
          const suffix = contextEnd < pdfTextContent.length ? '...' : '';
          
          // Bold the matching keyword in the context
          const beforeMatch = context.substring(0, matchIndex - contextStart);
          const match = context.substring(matchIndex - contextStart, matchIndex - contextStart + query_lower.length);
          const afterMatch = context.substring(matchIndex - contextStart + query_lower.length);
          const formattedContext = `${prefix}${beforeMatch}**${match}**${afterMatch}${suffix}`;
          
          results.push({
            id: `${doc.id}-match-${index}`,
            type: SEARCH_RESULT_TYPES.JOURNAL_PDF_CONTENT,
            title: data.title || 'Untitled Entry',
            subtitle: `PDF Match ${index + 1}${matches.length > maxMatchesPerDoc ? ` of ${matches.length}+` : ` of ${matches.length}`}`,
            description: formattedContext,
            exactMatch: false,
            createdAt: data.createdAt?.toDate?.() || null,
            navigationPath: `/view-pdf/${doc.id}`,
            navigationState: {
              pdfUrl: data.downloadURL,
              journalTitle: data.title,
              searchPhrase: query_lower,
              matchNumber: index + 1, // Which match this is (1-based)
              totalMatches: matches.length,
              from: '/benny-journal'
            },
            icon: '📄',
            matchCount: matches.length
          });
        });
      }
    });

    return results;
  } catch (error) {
    console.error('Error searching journal entries:', error);
    return [];
  }
};

/**
 * Search wiki entries (people, locations, items)
 */
const searchWikiEntries = async (query_lower) => {
  try {
    const querySnapshot = await getDocs(collection(db, 'wiki_entries'));
    const results = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const title = (data.title || '').toLowerCase();
      const description = (data.description || '').toLowerCase();
      const tags = (data.tags || []).map(tag => tag.toLowerCase());
      
      if (title.includes(query_lower) || 
          description.includes(query_lower) || 
          tags.some(tag => tag.includes(query_lower))) {
        
        // Get icon based on type
        let icon = '📖';
        if (data.type === 'person') icon = '👥';
        else if (data.type === 'location') icon = '📍';
        else if (data.type === 'item') icon = '⚔️';
        
        // Get type label
        const typeLabel = data.type ? data.type.charAt(0).toUpperCase() + data.type.slice(1) : 'Wiki Entry';
        
        results.push({
          id: doc.id,
          type: SEARCH_RESULT_TYPES.WIKI,
          title: data.title || 'Untitled Entry',
          subtitle: `${typeLabel}${data.tags?.length > 0 ? ` • ${data.tags.slice(0, 2).join(', ')}` : ''}`,
          description: description.substring(0, 100).replace(/<[^>]*>/g, ''), // Strip HTML tags
          exactMatch: title === query_lower,
          createdAt: data.createdAt?.toDate?.() || null,
          navigationPath: `/wiki/${doc.id}`,
          icon: icon
        });
      }
    });

    return results;
  } catch (error) {
    console.error('Error searching wiki entries:', error);
    return [];
  }
};

/**
 * Get search suggestions (for autocomplete)
 * Returns a limited number of quick suggestions
 */
export const getSearchSuggestions = async (searchQuery) => {
  if (!searchQuery || searchQuery.trim().length < 2) {
    return [];
  }

  try {
    const results = await performUnifiedSearch(searchQuery);
    // Return top 10 results for suggestions
    return results.slice(0, 10);
  } catch (error) {
    console.error('Error getting search suggestions:', error);
    return [];
  }
};
